import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import API from "../lib/api";
import { Users, Plus, Pencil, Ban, CheckCircle, Loader2, Shield, UserCheck } from "lucide-react";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  audit_manager: "Audit Manager",
  senior_auditor: "Senior Auditor",
  auditor: "Auditor",
  external_auditor: "External Auditor",
  control_owner: "Control Owner",
  observer: "Observer",
};

const ROLE_COLORS = {
  super_admin: "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-950/30 dark:text-violet-400 dark:border-violet-800",
  audit_manager: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800",
  senior_auditor: "bg-cyan-100 text-cyan-800 border-cyan-200 dark:bg-cyan-950/30 dark:text-cyan-400 dark:border-cyan-800",
  auditor: "bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800/30 dark:text-slate-300 dark:border-slate-700",
  external_auditor: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800",
  control_owner: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800",
  observer: "bg-zinc-100 text-zinc-800 border-zinc-200 dark:bg-zinc-900/50 dark:text-zinc-300 dark:border-zinc-700"
};

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", password: "", role: "auditor", department: "", process_areas_text: "" });

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await API.get("/users");
      setUsers(data);
    } catch (e) { toast.error("Failed to load users"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error("All fields are required");
      return;
    }
    if (newUser.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await API.post("/users", {
        email: newUser.email,
        name: newUser.name,
        password: newUser.password,
        role: newUser.role,
        department: newUser.department || null,
        process_areas: String(newUser.process_areas_text || "").split(",").map(item => item.trim()).filter(Boolean),
      });
      setAddOpen(false);
      setNewUser({ email: "", name: "", password: "", role: manageableRoles[0]?.value || "auditor", department: "", process_areas_text: "" });
      await fetchUsers();
      toast.success("User created");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    }
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      const payload = { name: editUser.name, role: editUser.role, status: editUser.status };
      if (editUser.newPassword) payload.password = editUser.newPassword;
      payload.department = editUser.department || "";
      payload.process_areas = String(editUser.process_areas_text || "").split(",").map(item => item.trim()).filter(Boolean);
      await API.put(`/users/${editUser.id}`, payload);
      setEditUser(null);
      await fetchUsers();
      toast.success("User updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
    setSaving(false);
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "disabled" : "active";
    try {
      await API.put(`/users/${userId}`, { status: newStatus });
      await fetchUsers();
      toast.success(`User ${newStatus}`);
    } catch (e) { toast.error("Failed to update status"); }
  };

  const manageableRoles = (currentUser?.manageable_roles || []).map((role) => ({
    value: role,
    label: ROLE_LABELS[role] || role,
  }));

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">User Management</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage users, roles, and access permissions.</p>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-user-btn">
          <Plus className="w-3 h-3 mr-1" /> Add User
        </Button>
      </div>

      {/* Role Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.entries(ROLE_LABELS).map(([role, label]) => {
          const count = users.filter(u => u.role === role && u.status === "active").length;
          return (
            <Card key={role}>
              <CardContent className="py-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-md flex items-center justify-center ${ROLE_COLORS[role]}`}>
                  {role === "super_admin" ? <Shield className="w-5 h-5" /> : role === "audit_manager" ? <UserCheck className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                </div>
                <div>
                  <p className="text-2xl font-bold font-mono">{count}</p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All Users ({users.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-muted-foreground" /></div>
          ) : (
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium text-sm">{u.name}</TableCell>
                    <TableCell className="font-mono text-xs">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-xs border ${ROLE_COLORS[u.role] || ''}`}>
                        {ROLE_LABELS[u.role] || u.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${u.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <span className="text-xs capitalize">{u.status}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => setEditUser({ ...u, newPassword: "", process_areas_text: (u.process_areas || []).join(", ") })}
                          disabled={u.id === currentUser?.id && currentUser?.role !== "super_admin"}
                          data-testid={`edit-user-${u.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        {u.id !== currentUser?.id && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => handleToggleStatus(u.id, u.status)}
                            data-testid={`toggle-user-${u.id}`}>
                            {u.status === 'active' ? <Ban className="w-3 h-3 text-destructive" /> : <CheckCircle className="w-3 h-3 text-emerald-500" />}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create New User</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <div><Label className="text-xs">Full Name</Label>
              <Input value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))} placeholder="John Doe" className="mt-1" data-testid="new-user-name" /></div>
            <div><Label className="text-xs">Email</Label>
              <Input type="email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="john@bank.com" className="mt-1" data-testid="new-user-email" /></div>
            <div><Label className="text-xs">Password</Label>
              <Input type="password" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} placeholder="Min 6 characters" className="mt-1" data-testid="new-user-password" /></div>
            <div><Label className="text-xs">Role</Label>
              <Select value={newUser.role} onValueChange={v => setNewUser(p => ({ ...p, role: v }))}>
                <SelectTrigger className="mt-1" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {manageableRoles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div><Label className="text-xs">Department</Label>
              <Input value={newUser.department} onChange={e => setNewUser(p => ({ ...p, department: e.target.value }))} placeholder="Finance, Operations..." className="mt-1" /></div>
            <div><Label className="text-xs">Process Areas</Label>
              <Input value={newUser.process_areas_text} onChange={e => setNewUser(p => ({ ...p, process_areas_text: e.target.value }))} placeholder="Payments, Returns" className="mt-1" /></div>
            <Button onClick={handleCreate} disabled={saving} className="w-full" data-testid="confirm-create-user-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={() => setEditUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-3 pt-1">
              <div><Label className="text-xs">Full Name</Label>
                <Input value={editUser.name} onChange={e => setEditUser(p => ({ ...p, name: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Email</Label>
                <Input value={editUser.email} disabled className="mt-1 font-mono opacity-60" /></div>
              {manageableRoles.length > 0 && (
                <div><Label className="text-xs">Role</Label>
                  <Select value={editUser.role} onValueChange={v => setEditUser(p => ({ ...p, role: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {manageableRoles.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              )}
              <div><Label className="text-xs">Department</Label>
                <Input value={editUser.department || ""} onChange={e => setEditUser(p => ({ ...p, department: e.target.value }))} className="mt-1" /></div>
              <div><Label className="text-xs">Process Areas</Label>
                <Input value={editUser.process_areas_text || ""} onChange={e => setEditUser(p => ({ ...p, process_areas_text: e.target.value }))} className="mt-1" placeholder="Payments, Returns" /></div>
              <div><Label className="text-xs">New Password (leave blank to keep current)</Label>
                <Input type="password" value={editUser.newPassword || ""} onChange={e => setEditUser(p => ({ ...p, newPassword: e.target.value }))} className="mt-1" placeholder="New password..." /></div>
              <Button onClick={handleUpdate} disabled={saving} className="w-full" data-testid="confirm-edit-user-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
